import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Visibility, VisibilityOff, Save } from "@material-ui/icons";
import { toast } from "react-toastify";
import useSettings from "../../hooks/useSettings";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  infoText: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  urlBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === "dark" ? "#333" : "#f5f5f5",
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
  },
  url: {
    fontFamily: "monospace",
    fontSize: "0.9rem",
    wordBreak: "break-all",
    color: theme.palette.primary.main,
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    display: "flex",
    justifyContent: "flex-end",
  },
}));

const InstagramSettings = () => {
  const classes = useStyles();
  const { getAll, update } = useSettings();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showVerifyToken, setShowVerifyToken] = useState(false);

  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [verifyToken, setVerifyToken] = useState("");
  const [redirectUri, setRedirectUri] = useState("");
  const [graphVersion, setGraphVersion] = useState("v23.0");
  const [managedByServer, setManagedByServer] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const webhookUrl = `${backendUrl}/webhook/instagram`;
  const defaultRedirectUri = `${backendUrl}/instagram/oauth/callback`;

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // O app da Meta configurado no .env do servidor tem precedência sobre
      // qualquer valor preenchido aqui.
      try {
        const { data } = await api.get("/meta/app-config");
        setManagedByServer(Boolean(data?.instagram?.enabled));
      } catch (_err) {
        setManagedByServer(false);
      }

      const settings = await getAll();
      if (Array.isArray(settings)) {
        const find = (k) => settings.find((s) => s.key === k);
        setAppId(find("instagramAppId")?.value || "");
        setAppSecret(find("instagramAppSecret")?.value || "");
        setVerifyToken(find("instagramVerifyToken")?.value || "");
        setRedirectUri(find("instagramRedirectUri")?.value || defaultRedirectUri);
        setGraphVersion(find("instagramGraphVersion")?.value || "v23.0");
      }
    } catch (err) {
      toast.error(i18n.t("settings.instagram.toasts.loadError"));
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await update({ key: "instagramAppId", value: appId });
      await update({ key: "instagramAppSecret", value: appSecret });
      await update({ key: "instagramVerifyToken", value: verifyToken });
      await update({ key: "instagramRedirectUri", value: redirectUri || defaultRedirectUri });
      await update({ key: "instagramGraphVersion", value: graphVersion });
      toast.success(i18n.t("settings.instagram.toasts.saved"));
    } catch (err) {
      toast.error(i18n.t("settings.instagram.toasts.saveError"));
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Paper className={classes.paper} elevation={0}>
        <Grid container justifyContent="center" style={{ padding: 40 }}>
          <CircularProgress />
        </Grid>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper} elevation={0}>
      <Typography variant="h6" className={classes.sectionTitle}>
        {i18n.t("settings.instagram.title")}
      </Typography>

      <Typography className={classes.infoText}>
        {i18n.t("settings.instagram.info.beforeProduct")} <strong>Instagram</strong>{" "}
        {i18n.t("settings.instagram.info.afterProduct")}{" "}
        <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer">
          Meta for Developers
        </a>
        {" "}{i18n.t("settings.instagram.info.path")}
      </Typography>

      {managedByServer && (
        <Typography
          className={classes.infoText}
          style={{ padding: 12, border: "1px solid #2e7d32", borderRadius: 4, color: "#2e7d32" }}
        >
          <strong>✅ {i18n.t("settings.instagram.managedByServer.title")}</strong>{" "}
          {i18n.t("settings.instagram.managedByServer.description")}
        </Typography>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Instagram App ID"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            fullWidth
            variant="outlined"
            margin="dense"
            helperText={i18n.t("settings.instagram.fields.appIdHelper")}
            placeholder={i18n.t("settings.instagram.fields.appIdPlaceholder")}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Instagram App Secret"
            value={appSecret}
            onChange={(e) => setAppSecret(e.target.value)}
            fullWidth
            variant="outlined"
            margin="dense"
            type={showSecret ? "text" : "password"}
            helperText={i18n.t("settings.instagram.fields.appSecretHelper")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowSecret(!showSecret)}>
                    {showSecret ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Webhook Verify Token"
            value={verifyToken}
            onChange={(e) => setVerifyToken(e.target.value)}
            fullWidth
            variant="outlined"
            margin="dense"
            type={showVerifyToken ? "text" : "password"}
            helperText={i18n.t("settings.instagram.fields.verifyTokenHelper")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowVerifyToken(!showVerifyToken)}>
                    {showVerifyToken ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel>{i18n.t("settings.instagram.fields.graphVersion")}</InputLabel>
            <Select
              value={graphVersion}
              onChange={(e) => setGraphVersion(e.target.value)}
              label={i18n.t("settings.instagram.fields.graphVersion")}
            >
              <MenuItem value="v25.0">v25.0 ({i18n.t("settings.instagram.fields.latest")})</MenuItem>
              <MenuItem value="v24.0">v24.0</MenuItem>
              <MenuItem value="v23.0">v23.0</MenuItem>
              <MenuItem value="v22.0">v22.0</MenuItem>
              <MenuItem value="v21.0">v21.0</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Redirect URI (OAuth)"
            value={redirectUri}
            onChange={(e) => setRedirectUri(e.target.value)}
            fullWidth
            variant="outlined"
            margin="dense"
            helperText={i18n.t("settings.instagram.fields.redirectUriHelper")}
            placeholder={defaultRedirectUri}
          />
        </Grid>
      </Grid>

      <div className={classes.urlBox}>
        <Typography variant="subtitle2" gutterBottom>
          {i18n.t("settings.instagram.webhookUrl")}
        </Typography>
        <Typography className={classes.url}>{webhookUrl}</Typography>
        <Typography variant="subtitle2" gutterBottom style={{ marginTop: 8 }}>
          {i18n.t("settings.instagram.suggestedRedirectUri")}
        </Typography>
        <Typography className={classes.url}>{defaultRedirectUri}</Typography>
      </div>

      <div className={classes.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} /> : <Save />}
        >
          {saving ? i18n.t("common.saving") : i18n.t("settings.instagram.save")}
        </Button>
      </div>
    </Paper>
  );
};

export default InstagramSettings;
